// Imports for external libraries go here.
import React, { FC, useReducer, useEffect, useMemo, isValidElement } from 'react';
import { useQuery } from '@apollo/client';
import { EditableComponent } from '@adobe/aem-react-editable-components';
// Imports for internal (to the monorepo) libraries go here,
// separated by a blank line from external imports.
// The closer the import is to the file the lower it should be in this list.
import { TabComponent } from '@marriott/mi-ui-library';
import { phoenixLegalPrivacyConsentQuery } from '@marriott/mi-legal-graphql';

import { generateApolloClientHeadersForUXLCalls } from '../../utils/uxlHelper';
import { usePreventPageExit } from '../../hooks/usePreventPageExit';
import { useAuthStore, useLegalGenericStore, usePrivacyRightsStore } from '../../store';
import { PrivacyRightsComponent, PrivacyRightsModal } from '../../molecules/PrivacyRightsComponent';
import { LegalTextWithLink } from '../../molecules/LegalTextWithLink';
import { Dropdown } from '../../molecules/Dropdown';

import { PrivacyRightsAlertComponent } from './PrivacyRightsAlertComponent';
import {
  PrivacyRightsMainComponentProps,
  PhoenixLegalPrivacyConsentResponseResult,
  PrivacyRightsLoadingSkeletonProps,
} from './PrivacyRightsWrapper.types';
import { StyledPrivacyRightsWrapper, GlobalStyles } from './PrivacyRightsWrapper.styles';
import {
  transFormPrivacyRightsWrapperAEMSchemaToPrivacyRightsMainComponentProps,
  transformCustomerData,
  capitalizeFirstLetter,
} from './utils/PrivacyRightsTransFormSchemaUtils';
import {
  PrivacyRightsMainComponentStateType,
  PrivacyRightsMainComponentActionType,
} from './reducer/PrivacyRightsMainComponentReducer.types';
import {
  privacyRightsMainComponentReducer,
  getPrivacyRightsMainComponentInitialState,
} from './reducer/PrivacyRightsMainComponentReducer';

const GET_PRIVACY_CONSENT_OPERATION_NAME = 'phoenixLegalPrivacyConsentQuery';

export const PrivacyRightsWrapperConfig = {
  emptyLabel: 'PrivacyRightsWrapper',
  isEmpty: () => true,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/privacyrightswrapper`,
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const PrivacyRightsWrapper = (props: any) => {
  const transformedSchema = transFormPrivacyRightsWrapperAEMSchemaToPrivacyRightsMainComponentProps(props.model);

  return (
    <EditableComponent config={PrivacyRightsWrapperConfig} {...props}>
      <GlobalStyles />
      <PrivacyRightsMainComponent {...transformedSchema} />
    </EditableComponent>
  );
};

const PrivacyRightsLoadingSkeleton: FC<PrivacyRightsLoadingSkeletonProps> = ({ className, isLoading, children }) =>
  isLoading ? <div className={`skeleton-loader ${className}`} /> : isValidElement(children) ? children : null;

const PrivacyRightsMainComponent: FC<PrivacyRightsMainComponentProps> = ({
  componentTitle,
  tabData,
  privacyChoiceAuthSubHeading,
  privacyChoiceUnAuthSubHeading,
  privacyChoiceNonUSResidentSubHeading,
  statesLabel,
  statesDropDownPlaceholder,
  userNamePrefix,
  stateNameSuffix,
  pendingChangesModalHeader,
  pendingChangesModalContent,
  optInConfirmationModalHeader,
  modalProceedButtonLabel,
  modalCancelButtonLabel,
  profileUpdateAlertMessage,
  profileUpdateAuthAlertMessage,
  profileNotFoundMessage,
  apiErrorMessage,
  authInEligibleAlertMessage,
  statesData,
}) => {
  const { isAuthenticated, sessionId } = useAuthStore();
  const { isAuthorMode, language, sessionData } = useLegalGenericStore();
  const {
    isFormHasPendingChange,
    setConfirmationModalGenericData,
    setAlertComponentConfig,
    setLoggedInUserPrivacyRightsData,
  } = usePrivacyRightsStore.getState();

  const [privacyRightsMainComponentStates, dispatch] = useReducer<
    React.Reducer<PrivacyRightsMainComponentStateType, PrivacyRightsMainComponentActionType>
  >(privacyRightsMainComponentReducer, getPrivacyRightsMainComponentInitialState(tabData[0]?.tabValue));

  const {
    selectedStateOrLoggedInUserStateCode,
    isUsStateAndBelongToCpraGroups,
    showFormResetConfirmationModal,
    selectedTab,
    userName,
  } = privacyRightsMainComponentStates;

  usePreventPageExit(isFormHasPendingChange);

  const { data: userPrivacyConsentData, loading: isUserPrivacyConsentDataLoading } =
    useQuery<PhoenixLegalPrivacyConsentResponseResult>(phoenixLegalPrivacyConsentQuery, {
      variables: {
        customerId: sessionData?.cacheData?.data?.consumerID,
      },
      context: generateApolloClientHeadersForUXLCalls(sessionId, GET_PRIVACY_CONSENT_OPERATION_NAME, language),
      skip: isAuthorMode || !isAuthenticated,
      onError(error) {
        console.error(`Something went wrong while fetching privacyConsent details: ${JSON.stringify(error)}`);

        setAlertComponentConfig({
          alertType: 'apiError',
          showAlertComponent: true,
        });
      },
    });

  useEffect(() => {
    setConfirmationModalGenericData({
      optInConfirmationModalHeader,
      modalProceedButtonLabel,
      modalCancelButtonLabel,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!userPrivacyConsentData) {
      return;
    }

    const transformedUserData = transformCustomerData(userPrivacyConsentData);

    if (!transformedUserData?.countryCode) {
      setAlertComponentConfig({
        alertType: 'apiError',
        showAlertComponent: true,
      });
      return;
    }

    const { name, stateCode, doNotSellOptIn, doNotShareOptIn } = transformedUserData;
    const isCpraState = statesData.some(state => state.stateCode === stateCode);

    dispatch({
      type: 'SET_MULTIPLE_PRIVACY_RIGHTS_STATES',
      payload: {
        userName: `${userNamePrefix} ${capitalizeFirstLetter(name)},`,
        isUsStateAndBelongToCpraGroups: isCpraState,
        selectedStateOrLoggedInUserStateCode: isCpraState ? (stateCode as string) : 'NA',
      },
    });

    if (isCpraState) {
      setLoggedInUserPrivacyRightsData({ doNotSellOptIn, doNotShareOptIn });
    } else {
      setAlertComponentConfig({
        alertType: 'authIneligible',
        showAlertComponent: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPrivacyConsentData]);

  const states = useMemo(
    () => [
      { label: statesDropDownPlaceholder, value: 'NA' },
      ...statesData.map(({ stateName, stateCode }) => ({ label: stateName, value: stateCode })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const { getNextState, setNextState } = useMemo(() => {
    let stateName: string | null;

    return {
      setNextState: (selectedState: string | null) => {
        stateName = selectedState;
      },
      getNextState: () => stateName,
    };
  }, []);

  const resetTab = () => {
    dispatch({
      type: 'SET_MULTIPLE_PRIVACY_RIGHTS_STATES',
      payload: {
        showFormResetConfirmationModal: false,
        selectedTab: tabData[0].tabValue,
      },
    });
  };

  const confirmStateChange = () => {
    const updatedState: Partial<PrivacyRightsMainComponentStateType> = {
      showFormResetConfirmationModal: false,
    };

    setAlertComponentConfig({ showAlertComponent: false });

    if (selectedTab === tabData[1].tabValue) {
      updatedState.isUsStateAndBelongToCpraGroups = false;
      updatedState.selectedStateOrLoggedInUserStateCode = 'NA';
    } else {
      const nextState = getNextState();
      if (nextState) {
        updatedState.selectedStateOrLoggedInUserStateCode = nextState;
      }
    }

    dispatch({ type: 'SET_MULTIPLE_PRIVACY_RIGHTS_STATES', payload: updatedState });

    setNextState(null);
  };

  const handleStateChange = (selectedState: string) => {
    if (isFormHasPendingChange()) {
      dispatch({ type: 'SET_SHOW_FORM_RESET_CONFIRMATION_MODAL', payload: true });
      setNextState(selectedState);
      return;
    }

    dispatch({ type: 'SET_SELECTED_STATE', payload: selectedState });

    setAlertComponentConfig({
      showAlertComponent: false,
    });
  };

  const handleUsNonUsResidentTabChange = (activeTabValue: string) => {
    const updates: Partial<PrivacyRightsMainComponentStateType> = {
      selectedTab: activeTabValue,
    };

    if (isFormHasPendingChange()) {
      updates.showFormResetConfirmationModal = true;
    } else {
      updates.isUsStateAndBelongToCpraGroups = activeTabValue === 'us';
      updates.selectedStateOrLoggedInUserStateCode = 'NA';
      setAlertComponentConfig({ showAlertComponent: false });
    }

    dispatch({ type: 'SET_MULTIPLE_PRIVACY_RIGHTS_STATES', payload: updates });
  };

  const getPrivacyChoiceSubHeading = () => {
    if (isUsStateAndBelongToCpraGroups) {
      return isAuthenticated ? privacyChoiceAuthSubHeading : privacyChoiceUnAuthSubHeading;
    }
    return privacyChoiceNonUSResidentSubHeading;
  };

  const shouldShowStatesDropdown = isUsStateAndBelongToCpraGroups && !isAuthenticated;
  const isAnyStateSelected = selectedStateOrLoggedInUserStateCode !== 'NA';

  return (
    <StyledPrivacyRightsWrapper
      data-testid="legal-PrivacyRightsWrapper"
      data-component-name="o-legal-PrivacyRightsWrapper"
    >
      <PrivacyRightsAlertComponent
        {...{
          profileUpdateAlertMessage,
          profileUpdateAuthAlertMessage,
          profileNotFoundMessage,
          apiErrorMessage,
          authInEligibleAlertMessage,
        }}
      />

      {isAuthenticated ? (
        <PrivacyRightsLoadingSkeleton isLoading={isUserPrivacyConsentDataLoading} className="userName-loader">
          <h4 className="display-name mt-4 pt-2" data-testid="legal-PrivacyRightsComponent-userName">
            {userName}
          </h4>
        </PrivacyRightsLoadingSkeleton>
      ) : (
        <>
          <h3 className="text-center">{componentTitle}</h3>
          <TabComponent
            tabList={tabData}
            selectedTab={selectedTab}
            setSelectedTab={handleUsNonUsResidentTabChange}
            isSwitcherTabs={true}
            classTabsEnabled="tab-disable-border"
            customClass="tab-height"
          />
        </>
      )}

      <PrivacyRightsLoadingSkeleton isLoading={isUserPrivacyConsentDataLoading} className="privacy-subHeading-loader">
        <LegalTextWithLink
          descriptionText={getPrivacyChoiceSubHeading()}
          className="privacy-statement"
          innerChildClassName="mt-3"
          testId="legal-privacy-statement-subheading"
        />
      </PrivacyRightsLoadingSkeleton>

      {shouldShowStatesDropdown && (
        <Dropdown
          label={statesLabel}
          className="states-dropdown"
          options={states}
          name="states"
          id="states"
          value={selectedStateOrLoggedInUserStateCode}
          handleChange={handleStateChange}
        />
      )}

      {isAnyStateSelected && (
        <PrivacyRightsComponent
          {...{
            statesData,
            selectedStateOrLoggedInUserStateCode,
            stateNameSuffix,
          }}
        />
      )}

      <PrivacyRightsLoadingSkeleton isLoading={isUserPrivacyConsentDataLoading} className="privacy-rights-loader" />

      {showFormResetConfirmationModal && (
        <PrivacyRightsModal
          showModal={showFormResetConfirmationModal}
          labelledBy="exit-modal-title"
          onClose={resetTab}
          modalHeaderContent={pendingChangesModalHeader}
          modalBodyContent={pendingChangesModalContent}
          modalProceedButtonLabel={modalProceedButtonLabel}
          modalCancelButtonLabel={modalCancelButtonLabel}
          onProceed={confirmStateChange}
        />
      )}
    </StyledPrivacyRightsWrapper>
  );
};
